// src/components/Dashboard/Inventory/Inventory_API.js

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const BASE_URL = 'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload?.exp && idToken.payload.exp <= currentTime) {
        console.error('Inventory API: ID token has expired.');
        return null;
      }

      return tokens.idToken.toString();
    }

    console.error('Inventory API: No ID token found in current session.');
    return null;
  } catch (error) {
    console.error('Inventory API: Failed to fetch auth session.', error);
    return null;
  }
};

const buildQueryString = params => {
  const query = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

export const inventoryDashboardApi = {
  /**
   * Fetches inventory dashboard summary data.
   * Mirrors the Flutter implementation for consistency.
   */
  getInventoryDashboard: async ({
    month,
    year,
    locationType,
    locationId,
  } = {}) => {
    const token = await getAuthToken();

    if (!token) {
      throw new Error(
        'Authentication token not found. Please log in to continue.'
      );
    }

    const queryParams = buildQueryString({
      month,
      year,
      location_type: locationType,
      location_id: locationId,
    });

    const url = `${BASE_URL}/inventory/dashboard${queryParams}`;
    console.log('Inventory Dashboard API: Request URL ->', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const responseBody = await response.json().catch(() => null);
      console.log(
        'Inventory Dashboard API: Response status ->',
        response.status
      );
      console.log('Inventory Dashboard API: Response body ->', responseBody);

      if (response.ok) {
        return {
          success: true,
          data: responseBody,
        };
      }

      if (response.status === 400) {
        const error = new Error(responseBody?.error || 'Bad request');
        error.status = response.status;
        error.endpoint = '/inventory/dashboard';
        
        // Log API error to logging service
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API error: ${response.status} for /inventory/dashboard`,
          error,
          {
            endpoint: '/inventory/dashboard',
            method: 'GET',
            status: response.status,
            responseData: responseBody,
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
        
        return {
          success: false,
          error: responseBody?.error || 'Bad request',
        };
      }

      if (response.status === 401) {
        const error = new Error('Unauthorized: Please re-authenticate.');
        error.status = response.status;
        error.endpoint = '/inventory/dashboard';
        
        // Log API error to logging service
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API error: ${response.status} Unauthorized for /inventory/dashboard`,
          error,
          {
            endpoint: '/inventory/dashboard',
            method: 'GET',
            status: response.status,
            responseData: responseBody,
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
        
        throw error;
      }

      if (response.status === 404) {
        const error = new Error('Inventory dashboard not found.');
        error.status = response.status;
        error.endpoint = '/inventory/dashboard';
        
        // Log API error to logging service
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API error: ${response.status} Not Found for /inventory/dashboard`,
          error,
          {
            endpoint: '/inventory/dashboard',
            method: 'GET',
            status: response.status,
            responseData: responseBody,
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
        
        throw error;
      }

      const error = new Error(
        `Failed to fetch inventory dashboard: ${response.status} - ${JSON.stringify(
          responseBody
        )}`
      );
      error.status = response.status;
      error.endpoint = '/inventory/dashboard';
      error.responseData = responseBody;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_dashboard',
        `Inventory Dashboard API error: ${response.status} for /inventory/dashboard`,
        error,
        {
          endpoint: '/inventory/dashboard',
          method: 'GET',
          status: response.status,
          responseData: responseBody,
        }
      ).catch(logError => {
        console.warn('[inventoryDashboardApi] Failed to log error:', logError);
      });
      
      throw error;
    } catch (error) {
      console.error('Inventory Dashboard API: Error ->', error);
      
      // Log network/fetch errors to logging service
      if (!error.status) {
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API network/fetch error for /inventory/dashboard`,
          error,
          {
            endpoint: '/inventory/dashboard',
            method: 'GET',
            errorType: error.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
      }
      
      throw error;
    }
  },

  /**
   * Fetches inventory status summary.
   * Mirrors the Flutter getInventoryStatus implementation.
   */
  getInventoryStatus: async ({ locationType, locationId } = {}) => {
    const token = await getAuthToken();

    if (!token) {
      throw new Error(
        'Authentication token not found. Please log in to continue.'
      );
    }

    const queryParams = buildQueryString({
      location_type: locationType,
      location_id: locationId,
    });

    const url = `${BASE_URL}/inventory/status${queryParams}`;
    console.log('Inventory Status API: Request URL ->', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const responseBody = await response.json().catch(() => null);
      console.log('Inventory Status API: Response status ->', response.status);
      console.log('Inventory Status API: Response body ->', responseBody);

      if (response.ok) {
        return {
          success: true,
          data: responseBody,
        };
      }

      const error = new Error(responseBody?.error || 'Unknown error');
      error.status = response.status;
      error.endpoint = '/inventory/status';
      error.responseData = responseBody;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_dashboard',
        `Inventory Dashboard API error: ${response.status} for /inventory/status`,
        error,
        {
          endpoint: '/inventory/status',
          method: 'GET',
          status: response.status,
          responseData: responseBody,
        }
      ).catch(logError => {
        console.warn('[inventoryDashboardApi] Failed to log error:', logError);
      });
      
      return {
        success: false,
        error: responseBody?.error || 'Unknown error',
        message: responseBody?.message || 'Request failed',
      };
    } catch (error) {
      console.error('Inventory Status API: Error ->', error);
      
      // Log network/fetch errors to logging service
      loggingService.logError(
        'api_error_inventory_dashboard',
        `Inventory Dashboard API network/fetch error for /inventory/status`,
        error,
        {
          endpoint: '/inventory/status',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryDashboardApi] Failed to log error:', logError);
      });
      
      return {
        success: false,
        error: 'Network error',
        message: error?.message || 'Failed to fetch inventory status',
      };
    }
  },

  /**
   * Fetches maintenance statistics.
   * Mirrors the Flutter getMaintenanceStatistics implementation.
   */
  getMaintenanceStatistics: async ({
    timePeriod,
    month,
    year,
    includeTrends = true,
    includeTechnicians = true,
  } = {}) => {
    const token = await getAuthToken();

    if (!token) {
      throw new Error(
        'Authentication token not found. Please log in to continue.'
      );
    }

    // Build query parameters with priority: month > year > timePeriod
    const queryParams = {};

    if (month != null) {
      queryParams.month = month;
    } else if (year != null) {
      queryParams.year = year.toString();
    } else if (timePeriod != null) {
      queryParams.time_period = timePeriod;
    }

    // Add optional parameters only if they're not the default (true)
    if (includeTrends !== true) {
      queryParams.include_trends = includeTrends.toString();
    }
    if (includeTechnicians !== true) {
      queryParams.include_technicians = includeTechnicians.toString();
    }

    const queryString = buildQueryString(queryParams);
    const url = `${BASE_URL}/inventory/dashboard/maintenance-stats${queryString}`;
    console.log('Maintenance Statistics API: Request URL ->', url);
    console.log(
      'Maintenance Statistics API: Headers: Authorization: Bearer',
      token.substring(0, 20) + '...'
    );

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const responseBody = await response.json().catch(() => null);
      console.log(
        'Maintenance Statistics API: Response status ->',
        response.status
      );
      console.log('Maintenance Statistics API: Response body ->', responseBody);

      if (response.status === 200) {
        console.log(
          'Maintenance Statistics API: Successfully retrieved data'
        );
        return {
          success: true,
          data: responseBody,
        };
      }

      if (response.status === 404) {
        const error = new Error('Maintenance statistics not found');
        error.status = response.status;
        error.endpoint = '/inventory/dashboard/maintenance-stats';
        
        // Log API error to logging service
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API error: ${response.status} Not Found for /inventory/dashboard/maintenance-stats`,
          error,
          {
            endpoint: '/inventory/dashboard/maintenance-stats',
            method: 'GET',
            status: response.status,
            responseData: responseBody,
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
        
        throw error;
      }

      const error = new Error(
        `Failed to fetch maintenance statistics: ${response.status} - ${JSON.stringify(
          responseBody
        )}`
      );
      error.status = response.status;
      error.endpoint = '/inventory/dashboard/maintenance-stats';
      error.responseData = responseBody;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_dashboard',
        `Inventory Dashboard API error: ${response.status} for /inventory/dashboard/maintenance-stats`,
        error,
        {
          endpoint: '/inventory/dashboard/maintenance-stats',
          method: 'GET',
          status: response.status,
          responseData: responseBody,
        }
      ).catch(logError => {
        console.warn('[inventoryDashboardApi] Failed to log error:', logError);
      });
      
      throw error;
    } catch (error) {
      console.error('=== ERROR IN MAINTENANCE STATISTICS API ===');
      console.error('Error:', error);
      
      // Log network/fetch errors to logging service
      if (!error.status) {
        loggingService.logError(
          'api_error_inventory_dashboard',
          `Inventory Dashboard API network/fetch error for /inventory/dashboard/maintenance-stats`,
          error,
          {
            endpoint: '/inventory/dashboard/maintenance-stats',
            method: 'GET',
            errorType: error.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[inventoryDashboardApi] Failed to log error:', logError);
        });
      }
      
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Error fetching maintenance statistics: ${error}`);
    }
  },
};
